import { AsyncResult } from '@/redux';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { CalculatePortfolioOptions } from '@/utils/portfolio/utils';
import { portfolioToComparisonItem, shouldUseMonthlyRollingInReturns } from '@/utils/comparison';
import { generateBSONId } from '@/utils/random';

import { getPortfolioHoldingsData, populatePortfolioMetrics } from '../edit-portfolio';
import { HoldingAllocationMode } from '@/enums/Portfolio';

import disclaimers from '../../../utils/disclaimers';
import Dictionary from '@/types/dictionary';
import ComparisonGroupProduct from '@/types/comparison-group-product';
import Comparison from '@/types/comparison';
import Portfolio from '@/types/portfolio';
import AppRegion from '@/types/app-region';
import ComparisonItem from '@/types/comparison-item';
import PortfolioHoldingType from '@/types/portfolio-holding-type';
import PortfolioHolding from '@/types/portfolio-holding';
import { selectCurrentTaxRate } from '../taxes/selectors';
import { ReduxState } from '../types';
import { recalcPortfolioItem } from './item-calculation';
import { getContactsOfComparisonItemGroup } from '@/utils/comparison/item-groups';

export const combineProductsIntoGroupItem =
  (
    products: ComparisonGroupProduct[],
    comparison: Comparison,
    portfolios: Dictionary<Portfolio>,
    state: ReduxState,
    appRegion: AppRegion,
  ): AsyncResult<ComparisonItem> =>
  async dispatch => {
    const totalSize = products.reduce((sum, item) => sum + item.dollars, 0);
    const totalFee = products.reduce((sum, item) => (!item.fee ? sum : sum + (item.dollars / totalSize) * item.fee), 0);

    // Create a temporary portfolio to calculate the group metrics - we'll then convert this
    // portfolio to a product groups comparison item.
    let portfolio: Portfolio = {
      dates: {},
      size: totalSize,
      discretionaryMer: totalFee,
      name: {
        'en-CA': '',
        'fr-CA': '',
      },
      holdings: products
        .reduce((holdings, groupItem) => {
          const compItem = comparison.items.find(item => item._id === groupItem.itemId);

          if (!compItem) {
            return holdings;
          }

          if (compItem.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
            const itemPortfolio = portfolios[compItem._id];
            if (itemPortfolio) {
              // Recalculate the weight of each underlying holding based on the total group
              // size - not just the portfolio
              holdings.push(
                ...itemPortfolio.holdings.map(h => {
                  const dollars = (h.weight * groupItem.dollars) / 100;
                  return {
                    ...h,
                    dollars,
                    weight: (dollars / totalSize) * 100,
                  };
                }),
              );
            }
          } else {
            holdings.push({
              _id: compItem._id,
              weight: (groupItem.dollars / totalSize) * 100,
              dollars: groupItem.dollars,
              symbol: compItem.holdings[0].symbol,
              name: compItem.name,
              original: {
                id: compItem._id,
                type: compItem.type as PortfolioHoldingType,
              },
            });
          }

          return holdings;
        }, [] as PortfolioHolding[])
        .filter(i => i != null) as PortfolioHolding[],
      allocationMode: HoldingAllocationMode.Weight,
    };

    const portfolioHoldingsData = await dispatch(getPortfolioHoldingsData(portfolio));

    // The tax rate is the weighted average of the tax rates of the underlying portfolios
    const taxRate = await Promise.all(
      products.map(async item => {
        const compItem = comparison.items.find(i => i._id === item.itemId);
        if (!compItem || compItem.contact == null) {
          return 0; // Return 0 if compItem or contact is not available
        }

        if (portfolios[compItem._id].contact) {
          const taxRate = selectCurrentTaxRate(state, portfolios[compItem._id].contact);
          return taxRate || 0;
        }

        return 0; // Return 0 if no tax is found
      }),
    );

    const totalTaxRate = taxRate.reduce((sum, rate) => sum + rate, 0);
    const weightedTaxRate = totalTaxRate / products.length;

    const options: CalculatePortfolioOptions = {
      showLongerHistory: comparison.shortHistory,
      taxRate: weightedTaxRate,
      commonInception: comparison?.commonInception,
    };

    portfolio = dispatch(populatePortfolioMetrics(portfolio, portfolioHoldingsData, options));

    const portfolioOptions = {
      taxRate: options.taxRate,
      shortHistory: options.showLongerHistory,
      comparison,
    };
    let portfolioItem = portfolioToComparisonItem(portfolio, appRegion, false, portfolioHoldingsData, portfolioOptions);
    portfolioItem = await recalcPortfolioItem(
      dispatch,
      state,
      portfolioItem,
      shouldUseMonthlyRollingInReturns(comparison, state.auth.user),
      comparison.shortHistory,
      comparison.showBaseCurrencies,
      true,
      comparison?.commonInception,
    );

    const contacts = getContactsOfComparisonItemGroup(products, portfolios);

    return {
      ...portfolioItem,
      _id: generateBSONId(),
      contactsInfo: contacts,
      type: COMPARISON_ITEM_TYPES.GROUP,
      groupItems: products,
      portfolioType: undefined,
      disclaimers: disclaimers.getComparisonPortfolioDisclaimers(
        portfolio,
        portfolioHoldingsData,
        false,
        comparison.shortHistory,
        comparison.commonInception,
        'GROUP',
      ),
    };
  };
